import './BatchOverrideConfirm.css';

/**
 * A8r C3 (2026-06-04) — Batch override confirmation modal.
 *
 * Lightweight confirmation dialog used by the TAROT Identification
 * section's "Clear All Overrides" button. The two apply-style batches
 * (Force All Identified / Force All Unknown) are non-destructive and
 * fire without confirmation — only the clear path needs a guard.
 *
 * Renders a backdrop + centered dialog with Confirm + Cancel buttons.
 * No external state — parent owns the open/close state via
 * conditional render. Accessibility: role="dialog" + aria-modal="true"
 * + aria-labelledby pointing at the title; focus management is left
 * to the parent (Esc handler not added — Cancel button is the explicit
 * exit).
 */
export interface BatchOverrideConfirmProps {
  /** Heading rendered at the top of the dialog. */
  title: string;
  /** Body text describing what will happen. */
  message: string;
  /** Affirmative button label (e.g., "Clear All Overrides"). */
  confirmLabel: string;
  /** Fired when the user confirms the action. */
  onConfirm: () => void;
  /** Fired when the user cancels OR clicks the backdrop. */
  onCancel: () => void;
}

export function BatchOverrideConfirm({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: BatchOverrideConfirmProps) {
  return (
    <div
      className="br-batch-override-confirm-backdrop"
      data-testid="br-batch-override-confirm-backdrop"
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="br-batch-override-confirm-dialog"
        data-testid="br-batch-override-confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="br-batch-override-confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="br-batch-override-confirm-title"
          className="br-batch-override-confirm-title br-label"
        >
          {title}
        </h2>
        <p className="br-batch-override-confirm-message br-label">{message}</p>
        <div className="br-batch-override-confirm-actions">
          <button
            type="button"
            className="br-batch-override-confirm-cancel br-label"
            data-testid="br-batch-override-confirm-cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="br-batch-override-confirm-confirm br-label"
            data-testid="br-batch-override-confirm-confirm"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BatchOverrideConfirm;
